/**
 * 启动流程（PROTOCOL.md §3 / §6）：
 * 1. 页面挂载先调 `app.info` 拿 `bridgeVersion` 做降级判断；
 * 2. 首屏结束（成功**或**失败）都要调 `app.ready`，原生据此收起骨架屏。
 */

import { useEffect, useState } from 'react';
import { app, bridge, type AppInfo } from './bridge';
import { setThemePreference } from './themeSync';

export type AppInfoState = {
  info: AppInfo | null;
  loading: boolean;
  error: string | null;
  /** 页面是否运行在 App 内（否则是浏览器 / mock） */
  isNative: boolean;
};

export function useAppInfo(): AppInfoState {
  const [info, setInfo] = useState<AppInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const value = await app.info();
        if (!cancelled) {
          setInfo(value);
          // App 内以原生主题为准
          if (value.theme === 'light' || value.theme === 'dark') {
            setThemePreference(value.theme);
          }
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
        // 注意：这里**不**调 app.ready()。首屏骨架要等页面数据就绪再撤，
        // 由页面用 useAppReady(首屏就绪) 触发。
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return { info, loading, error, isNative: bridge.isNative };
}
